import { findKey, includes } from 'lodash';

class Licenses {
  static DEFAULT = 'basic-monthly';

  static PLANS = {
    /* Follows the below format:
        plan: [monthly-planId, half-yearly-planId, yearly-planId]
      */

    basic: ['basic-monthly', 'basic-monthly', 'basic-monthly', 'basic-monthly'],
    lite: ['lite-monthly', 'lite-quarterly', 'lite-half-yearly', 'lite-yearly'],
    starter: [
      'lite-monthly',
      'lite-quarterly',
      'lite-half-yearly',
      'lite-yearly'
    ],
    plus: ['plus-monthly', 'plus-quarterly', 'plus-half-yearly', 'plus-yearly'],
    // pro: ['pro-monthly', 'pro-half-yearly', 'pro-yearly'],
    'premium-v2': [
      'premium-v2-monthly',
      'premium-v2-quarterly',
      'premium-v2-half-yearly',
      'premium-v2-yearly'
    ],
    premium: [
      'premium-monthly',
      'premium-quarterly',
      'premium-half-yearly',
      'premium-yearly'
    ],
    manager: [
      'manager-monthly',
      'manager-quarterly',
      'manager-half-yearly',
      'manager-yearly'
    ],
    dialer: [
      'dialer-monthly',
      'dialer-quarterly',
      'dialer-half-yearly',
      'dialer-yearly'
    ],
    enterprise: [
      'enterprise-monthly',
      'enterprise-quarterly',
      'enterprise-half-yearly',
      'enterprise-yearly'
    ],
    'enterprise-v2': [
      'enterprise-v2-monthly',
      'enterprise-v2-quarterly',
      'enterprise-v2-half-yearly',
      'enterprise-v2-yearly'
    ],
    startup: [
      'startup-monthly',
      'startup-half-yearly',
      'startup-quarterly',
      'startup-yearly'
    ],
    organization: [
      'organization-monthly',
      'organization-half-yearly',
      'organization-quarterly',
      'organization-yearly'
    ]
  };

  static GROUPS = {
    currentPlans: {
      lite: ['lite-monthly', 'lite-half-yearly', 'lite-yearly'],
      plus: ['plus-monthly', 'plus-half-yearly', 'plus-yearly'],
      business: ['premium-monthly', 'premium-half-yearly', 'premium-yearly'],
      startup: ['startup-monthly', 'startup-half-yearly', 'startup-yearly'],
      organization: [
        'organization-monthly',
        'organization-half-yearly',
        'organization-yearly'
      ],
      enterprise: [
        'enterprise-v2-monthly',
        'enterprise-v2-half-yearly',
        'enterprise-v2-yearly'
      ]
    },
    otherTeams: {
      basic: ['basic-monthly', 'basic-monthly', 'basic-monthly'],
      lite: ['lite-monthly', 'lite-half-yearly', 'lite-yearly'],
      plus: ['plus-monthly', 'plus-half-yearly', 'plus-yearly']
    },
    revenueTeams: {
      premium: [
        'premium-v2-monthly',
        'premium-v2-half-yearly',
        'premium-v2-yearly'
      ],
      business: ['premium-monthly', 'premium-half-yearly', 'premium-yearly'],
      enterprise: [
        'enterprise-monthly',
        'enterprise-half-yearly',
        'enterprise-yearly'
      ]
    }
  };

  static CYCLE = {
    monthly: 'monthly',
    yearly: 'yearly',
    halfYearly: 'half-yearly'
  };

  static FEATURES = {
    taskFromNotesAi: 'task_from_notes_ai',
    calScheduling: 'cal_scheduling',
    calendarIntegration: 'calendar_integration',
    callMonitoring: 'call_monitoring',
    conferencingIntegration: 'conferencing_integration',
    conversationInsightsDashboard: 'conversation_insights_dashboard',
    trendsDashboard: 'trends_dashboard',
    activityDashboard: 'activity_dashboard',
    trackersDashboard: 'trackers_dashboard',
    smartTracker: 'smart_tracker',
    topicsDashboard: 'topics_dashboard',
    crmIntegration: 'crm_integration',
    dialerIntegration: 'dialer_integration',
    keywordTracking: 'keyword_tracking',
    noteTaking: 'note_taking',
    recording: 'recording',
    externalMeetings: 'external_meetings',
    internalMeetings: 'internal_meetings',
    transcription: 'transcription',
    userManagement: 'user_mgmt',
    zoomCloudRecordingIntegration: 'zoom_cloud_recording_integration',
    realTimeTranscription: 'real_time_transcription',
    DealIntelligence: 'deal_intelligence',
    salesforceIntegration: 'crm_integration_salesforce',
    scorecards: 'scorecards',
    participantRecording: 'participant_recording',
    interactionDashboardWithoutFilters: 'interaction_dashboard_without_filters',
    interactionDashboardWithFilters: 'interaction_dashboard_with_filters',
    topQuestionsDashboard: 'top_questions_dashboard',
    slackIntegration: 'slack_integration',
    schedulerEventTitleCustomization: 'scheduler_event_title_customization',
    crmContactCreation: 'crm_contact_creation',
    clickupIntegration: 'clickup_integration',
    schedulerRouter: 'scheduler_smart_router',
    autoChapters: 'autochapters',
    engagementDashboard: 'engagement_dashboard',
    winLossAnalysis: 'win_loss_analysis',
    copilot: 'copilot',
    manualNoteSyncToCrm: 'note_to_crm_record_manual',
    smartPlaylists: 'playlists_smart',
    notesRegen: 'notes_regen',
    meetingCrmAssociation: 'meeting_crm_association',
    customSavedViews: 'custom_saved_views',
    downloadRecording: 'download_recording',
    comments: 'comments',
    snippets: 'snippets',
    playlists: 'playlists',
    schedulerOneOnOnePage: 'scheduler_one_on_one',
    schedulerGroupPage: 'scheduler_group',
    schedulerRoundRobinPage: 'scheduler_round_robin',
    schedulerManagedPages: 'scheduler_managed_pages',
    forecasting: 'forecasting',
    forecastSubmissionHistory: 'forecast_submission_history',
    schedulerCrmIntegration: 'scheduler_contact_creation',
    dealCopilot: 'deal_copilot',
    gmailSend: 'gmail_send',
    outlookSend: 'outlook_send',
    gmailSync: 'gmail_sync',
    outlookSync: 'outlook_sync',
    globalCopilot: 'global_copilot',
    rolePlayCoaching: 'role_play_coaching',
    emailAiReports: 'email_ai_reports',
    revIntelTopLevelFilter: 'rev_intel_top_level_filter',
    teamsV2: 'teams_v2',
    dealRiskRanker: 'deal_risk_ranker_fe',
    aiTasks: 'ai_tasks',
    googleConsentScreen: 'google_consent_screen'
  };

  static isExternalRecordingAllowed = features =>
    features && includes(features, Licenses.FEATURES.externalMeetings);

  static isInternalRecordingAllowed = features =>
    features && includes(features, Licenses.FEATURES.internalMeetings);

  static isRecordingAllowed = ({ features, meeting }) => {
    if (meeting) {
      if (meeting.isInternal) {
        return Licenses.isInternalRecordingAllowed(features);
      }
      return Licenses.isExternalRecordingAllowed(features);
    }
    return false;
  };

  static getPlanName = planId => {
    const plan = findKey(Licenses.PLANS, plans => includes(plans, planId));
    return plan === 'lite' ? 'starter' : plan;
  };

  /** Organization-level paid plans and above (organization + enterprise) */
  static isOrgLevelPlanOrAbove = planId =>
    planId &&
    (includes(Licenses.PLANS.organization, planId) ||
      includes(Licenses.PLANS.enterprise, planId) ||
      includes(Licenses.PLANS['enterprise-v2'], planId));

  // To be shown to existing customers
  static LEGACY_PLANS = [
    'lite-monthly',
    'lite-quarterly',
    'lite-half-yearly',
    'lite-yearly',
    'plus-monthly',
    'plus-quarterly',
    'plus-half-yearly',
    'plus-yearly',
    'premium-monthly',
    'premium-quarterly',
    'premium-half-yearly',
    'premium-yearly'
  ];

  static NEW_PLANS = [
    'enterprise-v2-monthly',
    'enterprise-v2-quarterly',
    'enterprise-v2-half-yearly',
    'enterprise-v2-yearly',
    'organization-monthly',
    'organization-quarterly',
    'organization-half-yearly',
    'organization-yearly',
    'startup-monthly',
    'startup-quarterly',
    'startup-half-yearly',
    'startup-yearly'
  ];
}
export default Licenses;
